import {
  validateObligationRecurrenceRule,
  type AdminLegalObligationRow,
  type LegalObligationAgencyFieldValue,
  type LegalObligationApplicabilityScopeValue,
  type LegalObligationResponsiblePartyValue,
  type LegalPositionKey,
  type ObligationRecurrenceValue,
  type ObligationDeadlineBasisValue,
  type ObligationEvidenceSourceValue,
  type ObligationIntervalAnchor,
  type ObligationIntervalUnit,
  type ObligationPeriodBasisValue,
  type ObligationRecurrenceRule,
  type ObligationRecurrenceRuleProblem,
  type UpdateAdminLegalObligationBody,
} from '@safetyhub/shared';

/**
 * 법정 의무 카탈로그 화면의 **순수 판정** — 미기재 칸 · 폼 상태 · 저장 본문.
 *
 * API 클라이언트(`legalObligationCatalog.ts`)와 가른 이유: 화면 spec이 그 모듈을 `vi.mock`하면
 * 팩토리가 준 키만 남아 여기 함수까지 사라진다(선례 `lib/memberStatus.ts`).
 */

// ── 미기재 판정 ──────────────────────────────────────────────────────────────

/**
 * 「미기재」로 세는 칸 — 실체화·임박 알림이 읽는 운영 축 + 「미정」이 기본값인 두 칸.
 *
 * 적용 범위·증빙 출처는 NULL이 아니라 `UNDETERMINED`(seed 기본값)로 비어 있다 — 그 값을 세지 않으면
 * 아무도 정하지 않은 의무가 「기재 완료」로 보인다(사용자 결정 2026-10-29: 「미정」도 미기재로 센다).
 */
export type CatalogMissingField =
  | 'recurrence'
  /** `PERIODIC`인데 단위·간격·기산 중 빈 것이 있다(②-b — 종전 「다년 간격」의 자리) */
  | 'intervalRule'
  | 'periodBasis'
  | 'deadlineBasis'
  | 'responsibleParty'
  | 'applicabilityScope'
  | 'evidenceSource';

export const CATALOG_MISSING_FIELD_LABEL: Record<CatalogMissingField, string> = {
  recurrence: '주기',
  intervalRule: '주기 간격·기산',
  periodBasis: '기간 기준',
  deadlineBasis: '기한 기준',
  responsibleParty: '담당 주체',
  applicabilityScope: '적용 범위',
  evidenceSource: '증빙 출처',
};

/**
 * 비어 있는 운영 칸 — 표시 순서대로.
 *
 * ⚠️ **NULL의 뜻이 칸마다 다르다** — 그래서 「미기재」와 「실체화가 막힌다」를 가른다
 * ({@link blocksMaterialization}). shared `resolveObligationPeriod`가 정본이다:
 *   · `recurrence` NULL → `RECURRENCE_UNSET`으로 **skip**(인스턴스가 생기지 않는다)
 *   · `periodBasis` NULL → **연도로 읽는다**(skip 아님 — 건설 현장이면 거짓 연간 인스턴스가 될 수 있다)
 *   · `deadlineBasis` NULL → 기간 끝(`periodEnd`)으로 읽는다
 *   · `responsibleParty` NULL → 실체화는 되지만 임박 알림의 수신 주체가 갈리지 않는다
 *   · (`agencyField`는 2026-11-03부터 **NOT NULL**이라 이 목록에 없다 — 「어느 분야든」은 `ANY`가 맡고 비어 있을 수 없다.
 *     종전 「NULL = 미검토」 갈래는 칸과 함께 걷혔다)
 * ⇒ 넷 다 「채워야 할 칸」이지만 **차단은 주기 하나**다. 둘을 한 배지로 합치면 운영자가 「주기만
 *   채우면 된다」와 「넷 다 채워야 보인다」 중 무엇인지 모른다.
 * 주기 간격·기산(`intervalRule`)은 `PERIODIC`일 때만 센다(다른 종류에서는 쓰이지 않는다 — ②-b). DB CHECK가 그 조합을 거부하므로
 * 정상 데이터에서는 0이고, 0이 아니면 CHECK 이전 배포 창의 형상이다.
 * 적용 범위·증빙 출처는 `UNDETERMINED`일 때 센다 — 실체화를 막지 않으므로 차단 배지와 무관하다.
 */
export function missingCatalogFields(
  row: Pick<
    AdminLegalObligationRow,
    | 'recurrence'
    | 'intervalUnit'
    | 'intervalCount'
    | 'intervalAnchor'
    | 'periodBasis'
    | 'deadlineBasis'
    | 'responsibleParty'
    | 'applicabilityScope'
    | 'evidenceSource'
  >,
): CatalogMissingField[] {
  const missing: CatalogMissingField[] = [];
  if (row.recurrence === null) missing.push('recurrence');
  if (row.recurrence === 'PERIODIC' && (row.intervalUnit === null || row.intervalCount === null || row.intervalAnchor === null)) {
    missing.push('intervalRule');
  }
  if (row.periodBasis === null) missing.push('periodBasis');
  if (row.deadlineBasis === null) missing.push('deadlineBasis');
  if (row.responsibleParty === null) missing.push('responsibleParty');
  if (row.applicabilityScope === 'UNDETERMINED') missing.push('applicabilityScope');
  if (row.evidenceSource === 'UNDETERMINED') missing.push('evidenceSource');
  return missing;
}

/**
 * 주기가 비어 실체화가 skip되나 — shared `resolveObligationPeriod`의 `RECURRENCE_UNSET` 분기와 같은 축(옛 값 축은 ②-e
 * 마이그레이션 D가 enum에서 값을 지워 사라졌다 · 2026-11-03). ⚠️ `NONE`(상시)은 차단이 아니다 — 검토했더니 주기가 없는 것이고
 * 그 skip(`STANDING_DUTY`)은 정상 분류다.
 */
export function blocksMaterialization(row: Pick<AdminLegalObligationRow, 'recurrence'>): boolean {
  return row.recurrence === null;
}

/**
 * 개정 축 칸 이름의 화면 문구 — 이력의 `changedFields`(서버 `CATALOG_REVISION_FIELDS`의 원소)를 읽는다.
 * 모르는 이름이 오면 {@link revisionFieldLabel}이 그 이름을 그대로 보인다(숨기지 않는다).
 * 키 = 편집 폼 칸(= PATCH 칸 = 서버 `CATALOG_PATCHABLE_FIELDS` = 개정 축 · W1에서 차집합 0) + 줄 단위 개정 토큰 둘(`conditions`·`intervalVariants` —
 * 조건·변형 줄 쓰기가 `changedFields`에 싣는 이름 · 서버 `ChildRevisionToken`).
 * (종전 `MigrationOnlyRevisionField` 넷 — W1이 4칸을 PATCH로 열어 W3 2026-11-04 폼에 들어왔다. 서버에 마이그레이션 전용 칸이 다시 생기면
 *  그 칸을 여기 별 키 묶음으로 되살린다 — 서버 `CATALOG_PATCHABLE_FIELDS` docblock)
 */
export const CATALOG_REVISION_FIELD_LABEL: Record<
  // `obligor`(2026-11-05 ②-g3 G4) — 서버 개정 축의 «마이그레이션 전용» 칸(PATCH·폼 밖 — 위 docblock의 「별 키 묶음」이 되살아났다)
  Exclude<keyof CatalogFormState, 'changeReason'> | 'conditions' | 'intervalVariants' | 'obligor',
  string
> = {
  title: '문서명',
  description: '설명',
  statuteCitation: '근거 법령 인용',
  statuteLawName: '법령명',
  statuteArticleRef: '조·항·호',
  applicabilityScope: '적용 범위',
  responsibleParty: '담당 주체',
  agencyField: '대행 분야',
  recurrence: '주기 종류',
  intervalUnit: '반복 간격 단위',
  intervalCount: '반복 간격',
  intervalAnchor: '기산',
  initialIntervalUnit: '최초 간격 단위',
  initialIntervalCount: '최초 간격',
  cycleWindowMonths: '허용 창(개월)',
  eventDeadlineUnit: '사건 기한 단위',
  eventDeadlineCount: '사건 기한',
  deadlineBasis: '기한 기준',
  periodBasis: '기간 기준',
  evidenceSource: '증빙 출처',
  legalPosition: '법정 선임 직위',
  sourceNote: '출처 메모',
  isActive: '활성',
  subjectLegalPositions: '대상 직위',
  subjectProcessHazards: '공정·유해인자 대상',
  eventSourceObligationKey: '사건 공유 원천 의무',
  eventSourceVariantIds: '사건 공유 원천 변형',
  anchorSourceObligationKey: '기준일 선행 의무',
  obligor: '의무 주체',
  conditions: '적용 조건 줄',
  intervalVariants: '간격 변형 줄',
};

export function revisionFieldLabel(field: string): string {
  return (CATALOG_REVISION_FIELD_LABEL as Record<string, string | undefined>)[field] ?? field;
}

// ── 폼 ─────────────────────────────────────────────────────────────────────

/**
 * 편집 폼 상태 — select·input이 다루기 쉬운 형상(빈 값 = `''`).
 *
 * ⚠️ 칸 집합은 서버 `UpdateLegalObligationDto`와 같다(= PATCH 칸 — 정본 서버 `CATALOG_PATCHABLE_FIELDS` = 개정 축 · W1 2026-11-03부터 차집합 0). `key`·`lastVerifiedAt`·
 * `displayOrder`·`smsEscalation`은 **고칠 칸이 없다**(그 넷을 여는 판정은 DTO docblock의 ⓐⓑⓒ다).
 */
export interface CatalogFormState {
  title: string;
  description: string;
  statuteCitation: string;
  statuteLawName: string;
  statuteArticleRef: string;
  applicabilityScope: LegalObligationApplicabilityScopeValue;
  responsibleParty: LegalObligationResponsiblePartyValue | '';
  /** NOT NULL 칸(2026-11-03) — 빈 선택지가 없다(「어느 분야든」 = `ANY`) */
  agencyField: LegalObligationAgencyFieldValue;
  /** 지금 넷만 고른다(②-b) — 옛 5값은 선택지에 없고 그 값을 가진 행은 빈 선택으로 열린다(저장하면 NULL이 간다 · 서버가 옛 값을 거부한다) */
  recurrence: ObligationRecurrenceValue | '';
  intervalUnit: ObligationIntervalUnit | '';
  /** 숫자 문자열 — 빈 값이면 NULL */
  intervalCount: string;
  intervalAnchor: ObligationIntervalAnchor | '';
  initialIntervalUnit: ObligationIntervalUnit | '';
  initialIntervalCount: string;
  cycleWindowMonths: string;
  eventDeadlineUnit: ObligationIntervalUnit | '';
  eventDeadlineCount: string;
  deadlineBasis: ObligationDeadlineBasisValue | '';
  periodBasis: ObligationPeriodBasisValue | '';
  evidenceSource: ObligationEvidenceSourceValue;
  legalPosition: LegalPositionKey | '';
  sourceNote: string;
  isActive: boolean;
  /**
   * 대상 직위(W1 PATCH 칸 · W3 화면) — 순서가 곧 저장·표시 순서다(서버 비교가 순서를 본다). ⛔ `subjectProcessHazards`와 배타 —
   * 화면은 「대상」 라디오({@link subjectModeOf})로 한쪽만 고르게 하고, 서버가 저장 뒤 형상(`SUBJECT_EXCLUSIVE`)으로 한 번 더 막는다
   */
  subjectLegalPositions: LegalPositionKey[];
  subjectProcessHazards: boolean;
  /** 사건 공유 원천 의무의 `key` — `''` = 없음(자기 사건) */
  eventSourceObligationKey: string;
  /** 원천의 `EVENT_CHOICE` 변형 id 중 따라가는 것 — 원천이 비면 비어야 한다({@link formBlockedReason}) */
  eventSourceVariantIds: string[];
  /** 기준일 선행 의무의 `key`(2026-11-04 — 보수교육 ← 신규교육) — `''` = 없음(사람이면 배정일 기준) */
  anchorSourceObligationKey: string;
  /** 개정 사유 — 카탈로그 칸이 아니라 **이력 행**에 적힌다 */
  changeReason: string;
}

/** 「대상」 배타 선택 — 폼의 두 칸에서 파생한다(셋째 상태를 따로 들지 않는다 · 두 칸이 둘 다 차 있으면 옛 데이터의 위반이라 `BOTH`) */
export type CatalogSubjectMode = 'SITE' | 'POSITIONS' | 'PROCESS_HAZARDS' | 'BOTH';

export function subjectModeOf(form: Pick<CatalogFormState, 'subjectLegalPositions' | 'subjectProcessHazards'>): CatalogSubjectMode {
  const positions = form.subjectLegalPositions.length > 0;
  if (positions && form.subjectProcessHazards) return 'BOTH';
  if (positions) return 'POSITIONS';
  if (form.subjectProcessHazards) return 'PROCESS_HAZARDS';
  return 'SITE';
}

export function rowToForm(row: AdminLegalObligationRow): CatalogFormState {
  return {
    title: row.title,
    description: row.description ?? '',
    statuteCitation: row.statuteCitation,
    statuteLawName: row.statuteLawName ?? '',
    statuteArticleRef: row.statuteArticleRef ?? '',
    applicabilityScope: row.applicabilityScope,
    responsibleParty: row.responsibleParty ?? '',
    agencyField: row.agencyField,
    recurrence: row.recurrence ?? '',
    intervalUnit: row.intervalUnit ?? '',
    intervalCount: numberText(row.intervalCount),
    intervalAnchor: row.intervalAnchor ?? '',
    initialIntervalUnit: row.initialIntervalUnit ?? '',
    initialIntervalCount: numberText(row.initialIntervalCount),
    cycleWindowMonths: numberText(row.cycleWindowMonths),
    eventDeadlineUnit: row.eventDeadlineUnit ?? '',
    eventDeadlineCount: numberText(row.eventDeadlineCount),
    deadlineBasis: row.deadlineBasis ?? '',
    periodBasis: row.periodBasis ?? '',
    evidenceSource: row.evidenceSource,
    legalPosition: row.legalPosition ?? '',
    sourceNote: row.sourceNote ?? '',
    isActive: row.isActive,
    subjectLegalPositions: [...row.subjectLegalPositions],
    subjectProcessHazards: row.subjectProcessHazards,
    eventSourceObligationKey: row.eventSourceObligationKey ?? '',
    eventSourceVariantIds: [...row.eventSourceVariantIds],
    anchorSourceObligationKey: row.anchorSourceObligationKey ?? '',
    changeReason: '',
  };
}

/**
 * 기준일 선행 의무 «후보»(2026-11-04) — 서버 저장 뒤 형상(`ANCHOR_SOURCE_*`)과 같은 축: 자기 자신이 아니고 · 사람 대상(대상 직위가 있다)이고 ·
 * 활성이고 · 스스로 선행을 갖지 않는(한 단계) 의무. 지금 고른 값은 후보가 아니어도 목록에 남긴다(`eventSourceCandidates`와 같은 이유).
 */
export function anchorSourceCandidates(
  rows: readonly AdminLegalObligationRow[],
  self: Pick<AdminLegalObligationRow, 'key'>,
  currentKey: string,
): AdminLegalObligationRow[] {
  return rows.filter(
    (r) =>
      r.key !== self.key &&
      ((r.subjectLegalPositions.length > 0 && r.isActive && r.anchorSourceObligationKey === null) || r.key === currentKey),
  );
}

/**
 * 사건 공유 원천 «후보» — 서버 저장 뒤 형상 규칙(`legal-obligation-catalog-shape.ts`)과 같은 축으로 고른다: 자기 자신이 아니고 · 주기가
 * 「사유 발생 시」이고 · 활성이고 · 스스로 원천을 갖지 않는(공유는 한 단계) 의무. ⚠️ 지금 고른 값은 후보가 아니어도 목록에 남긴다 —
 * 빼면 옛 값이 조용히 「없음」으로 보이고 저장 때 지워진다(서버가 그 값의 문제를 400 사유로 알린다).
 */
export function eventSourceCandidates(
  rows: readonly AdminLegalObligationRow[],
  self: Pick<AdminLegalObligationRow, 'key'>,
  currentKey: string,
): AdminLegalObligationRow[] {
  return rows.filter(
    (r) =>
      r.key !== self.key &&
      ((r.recurrence === 'ON_EVENT' && r.isActive && r.eventSourceObligationKey === null) || r.key === currentKey),
  );
}

/** 원천의 `EVENT_CHOICE` 변형 — 공유 행이 따라갈 수 있는 사건 선택지(서버 `EVENT_VARIANT_NOT_IN_SOURCE`의 분모) */
export function eventChoiceVariantsOf(source: AdminLegalObligationRow | undefined) {
  return source ? source.intervalVariants.filter((v) => v.selector === 'EVENT_CHOICE') : [];
}

/** 빈 문자열(공백만 포함)을 NULL로 — nullable 칸은 「비운다」가 NULL이다 */
function nullableText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function numberText(value: number | null): string {
  return value === null ? '' : String(value);
}

/** 숫자 문자열 → 정수 또는 NULL(빈 값). 숫자가 아니면 `NaN`(검증이 잡는다) */
function nullableInt(value: string): number | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : Number(trimmed);
}

function interval(unit: ObligationIntervalUnit | '', count: string): ObligationRecurrenceRule['interval'] {
  const n = nullableInt(count);
  return unit === '' || n === null ? null : { unit, count: n };
}

/** 폼 → 규칙 객체 — 검증(`validateObligationRecurrenceRule`)과 미리보기 표시의 입력 */
export function formToRule(form: CatalogFormState): ObligationRecurrenceRule {
  return {
    recurrence: form.recurrence === '' ? null : form.recurrence,
    interval: interval(form.intervalUnit, form.intervalCount),
    intervalAnchor: form.intervalAnchor === '' ? null : form.intervalAnchor,
    initialInterval: interval(form.initialIntervalUnit, form.initialIntervalCount),
    cycleWindowMonths: nullableInt(form.cycleWindowMonths),
    eventDeadline: interval(form.eventDeadlineUnit, form.eventDeadlineCount),
  };
}

/** 규칙 모순의 화면 문구 — shared 사유 이름 전수(`Record`라 사유가 늘면 tsc가 막는다) */
export const RECURRENCE_RULE_PROBLEM_LABEL: Record<ObligationRecurrenceRuleProblem, string> = {
  INTERVAL_MISSING: '주기적이면 반복 간격(단위·수)을 넣어야 합니다.',
  ANCHOR_MISSING: '주기적이면 기산(달력 기준·기준일부터)을 골라야 합니다.',
  INTERVAL_COUNT_INVALID: '간격·창은 1 이상의 정수여야 합니다(사건 기한은 0 이상).',
  CALENDAR_INTERVAL_UNSUPPORTED: '달력 기준의 달 간격은 1·2·3·4·6 중 하나, 년 간격은 1이어야 합니다.',
  SUB_MONTH_MUST_BE_CALENDAR: '일·주 간격은 달력 기준(월별 묶음)만 됩니다.',
  ROLLING_ONLY_FIELD_ON_CALENDAR: '최초 간격·허용 창은 「기준일부터」에서만 쓸 수 있습니다.',
  FIELD_NOT_ALLOWED_FOR_KIND: '주기 칸은 「주기적」에서만, 사건 기한은 「사유 발생 시」에서만 쓸 수 있습니다.',
};

/** 저장을 막는 사유 — 있으면 저장 버튼이 비활성이다. 없으면 `undefined` */
export function formBlockedReason(form: CatalogFormState): string | undefined {
  if (form.title.trim() === '') return '문서명은 비울 수 없습니다.';
  if (form.statuteCitation.trim() === '') return '근거 법령 인용은 비울 수 없습니다.';
  // 단위만 고르고 수를 비우면(또는 반대) 규칙 객체에서 그 간격이 사라져 검증이 「없다」로 읽는다 — 짝이 안 맞는 것을 먼저 잡는다
  const pairs: Array<[ObligationIntervalUnit | '', string, string]> = [
    [form.intervalUnit, form.intervalCount, '반복 간격'],
    [form.initialIntervalUnit, form.initialIntervalCount, '최초 간격'],
    [form.eventDeadlineUnit, form.eventDeadlineCount, '사건 기한'],
  ];
  for (const [unit, count, label] of pairs) {
    // 조사 「은」은 위 세 라벨(간격·간격·기한)이 모두 받침으로 끝나서다 — 받침 없는 라벨을 더하면 shared `withTopicParticle`로
    if ((unit === '') !== (count.trim() === '')) return `${label}은 단위와 수를 함께 넣거나 함께 비워야 합니다.`;
    if (count.trim() !== '' && !Number.isInteger(Number(count))) return `${label}은 정수여야 합니다.`;
  }
  if (form.cycleWindowMonths.trim() !== '' && !Number.isInteger(Number(form.cycleWindowMonths))) return '허용 창은 정수(개월)여야 합니다.';
  // 대상·사건 공유(W3) — 서버 저장 뒤 형상과 같은 규칙 중 «폼만 보고 닫히는» 셋. 원천의 상태(주기·활성·연쇄)와 역방향은 서버가 판정한다
  if (subjectModeOf(form) === 'BOTH') return '대상 직위와 공정·유해인자 대상은 함께 지정할 수 없습니다. 한쪽을 비워 주세요.';
  if (form.eventSourceVariantIds.length > 0 && form.eventSourceObligationKey === '') return '원천 변형을 고르려면 사건 공유 원천 의무를 함께 지정해야 합니다.';
  if (form.eventSourceObligationKey !== '' && form.recurrence !== 'ON_EVENT') return '사건 공유 원천은 주기가 「사유 발생 시」인 의무에만 지정할 수 있습니다.';
  // 기준일 선행 의무(2026-11-04) — 서버 `ANCHOR_SOURCE_REQUIRES_ROLLING`·`ANCHOR_SOURCE_REQUIRES_PERSON_SUBJECT`와 같은 둘(선행 행의 상태는 서버가 판정한다)
  if (form.anchorSourceObligationKey !== '' && (form.recurrence !== 'PERIODIC' || form.intervalAnchor !== 'ROLLING')) {
    return '기준일 선행 의무는 주기가 「주기적 · 기준일부터」인 의무에만 지정할 수 있습니다.';
  }
  if (form.anchorSourceObligationKey !== '' && form.subjectLegalPositions.length === 0) return '기준일 선행 의무는 대상 직위가 있는 의무에만 지정할 수 있습니다.';
  // 서버가 저장 시점에 같은 함수로 검증한다(DTO 범위 + 합성 규칙) — 여기서 먼저 막아 왕복을 줄인다
  const problems = validateObligationRecurrenceRule(formToRule(form));
  if (problems.length > 0) return RECURRENCE_RULE_PROBLEM_LABEL[problems[0]];
  return undefined;
}

/**
 * 저장 본문 — **달라진 칸만** 싣는다(+ 사유).
 *
 * ⚠️ 서버도 같은 값이면 no-op이지만, 화면이 전량을 보내면 「무엇을 고쳤나」가 요청에 안 보이고
 * 정규화(공백 정리) 차이로 **고치지 않은 칸이 개정으로 기록**될 수 있다(제목 끝 공백 하나가 개정 1회가
 * 된다). 그래서 비교는 **정규화한 값끼리** 한다.
 * ⚠️ NOT NULL 칸(`title`·`statuteCitation`·`applicabilityScope`·`evidenceSource`·`isActive`)에는 NULL을
 * 넣지 않는다 — 서버가 400으로 거부한다(빈 제목은 {@link formBlockedReason}이 먼저 막는다).
 * 칸이 하나도 안 바뀌었으면 `null`을 돌려준다(보낼 것이 없다 — 사유만으로는 개정이 아니다).
 */
export function buildUpdateBody(
  row: AdminLegalObligationRow,
  form: CatalogFormState,
): UpdateAdminLegalObligationBody | null {
  const body: UpdateAdminLegalObligationBody = {};

  // ⚠️ 비교의 «양쪽»을 같은 규칙으로 정규화한다 — 종전엔 폼만 trim하고 행은 저장값 그대로 비교해,
  //    저장값에 앞뒤 공백이 있는 칸은 **손대지 않아도** 「바뀐 칸」으로 실려 개정이 기록됐다(리뷰 1103 F10).
  //    폼의 초기값(`rowToForm`)은 행 값을 그대로 넣으므로 정규화가 한쪽뿐이면 같은 값이 다르게 읽힌다.
  const title = form.title.trim();
  if (title !== row.title.trim()) body.title = title;
  const statuteCitation = form.statuteCitation.trim();
  if (statuteCitation !== row.statuteCitation.trim()) body.statuteCitation = statuteCitation;

  const description = nullableText(form.description);
  if (description !== nullableText(row.description ?? '')) body.description = description;
  const statuteLawName = nullableText(form.statuteLawName);
  if (statuteLawName !== nullableText(row.statuteLawName ?? '')) body.statuteLawName = statuteLawName;
  const statuteArticleRef = nullableText(form.statuteArticleRef);
  if (statuteArticleRef !== nullableText(row.statuteArticleRef ?? '')) body.statuteArticleRef = statuteArticleRef;
  const sourceNote = nullableText(form.sourceNote);
  if (sourceNote !== nullableText(row.sourceNote ?? '')) body.sourceNote = sourceNote;

  if (form.applicabilityScope !== row.applicabilityScope) {
    body.applicabilityScope = form.applicabilityScope;
  }
  if (form.evidenceSource !== row.evidenceSource) body.evidenceSource = form.evidenceSource;
  if (form.isActive !== row.isActive) body.isActive = form.isActive;

  const responsibleParty = form.responsibleParty === '' ? null : form.responsibleParty;
  if (responsibleParty !== row.responsibleParty) body.responsibleParty = responsibleParty;
  if (form.agencyField !== row.agencyField) body.agencyField = form.agencyField;
  const recurrence = form.recurrence === '' ? null : form.recurrence;
  if (recurrence !== row.recurrence) body.recurrence = recurrence;
  // 주기 규칙 칸 8개(②-b) — 달라진 칸만
  const intervalUnit = form.intervalUnit === '' ? null : form.intervalUnit;
  if (intervalUnit !== row.intervalUnit) body.intervalUnit = intervalUnit;
  const intervalCount = nullableInt(form.intervalCount);
  if (intervalCount !== row.intervalCount) body.intervalCount = intervalCount;
  const intervalAnchor = form.intervalAnchor === '' ? null : form.intervalAnchor;
  if (intervalAnchor !== row.intervalAnchor) body.intervalAnchor = intervalAnchor;
  const initialIntervalUnit = form.initialIntervalUnit === '' ? null : form.initialIntervalUnit;
  if (initialIntervalUnit !== row.initialIntervalUnit) body.initialIntervalUnit = initialIntervalUnit;
  const initialIntervalCount = nullableInt(form.initialIntervalCount);
  if (initialIntervalCount !== row.initialIntervalCount) body.initialIntervalCount = initialIntervalCount;
  const cycleWindowMonths = nullableInt(form.cycleWindowMonths);
  if (cycleWindowMonths !== row.cycleWindowMonths) body.cycleWindowMonths = cycleWindowMonths;
  const eventDeadlineUnit = form.eventDeadlineUnit === '' ? null : form.eventDeadlineUnit;
  if (eventDeadlineUnit !== row.eventDeadlineUnit) body.eventDeadlineUnit = eventDeadlineUnit;
  const eventDeadlineCount = nullableInt(form.eventDeadlineCount);
  if (eventDeadlineCount !== row.eventDeadlineCount) body.eventDeadlineCount = eventDeadlineCount;
  const deadlineBasis = form.deadlineBasis === '' ? null : form.deadlineBasis;
  if (deadlineBasis !== row.deadlineBasis) body.deadlineBasis = deadlineBasis;
  const periodBasis = form.periodBasis === '' ? null : form.periodBasis;
  if (periodBasis !== row.periodBasis) body.periodBasis = periodBasis;
  const legalPosition = form.legalPosition === '' ? null : form.legalPosition;
  if (legalPosition !== row.legalPosition) body.legalPosition = legalPosition;
  // 대상·사건 공유 넷(W3) — 배열은 원소를 순서대로 비교한다(서버 `sameRevisionValue`와 같은 축 — 순서만 바뀌어도 다른 정의다)
  if (!sameList(form.subjectLegalPositions, row.subjectLegalPositions)) body.subjectLegalPositions = [...form.subjectLegalPositions];
  if (form.subjectProcessHazards !== row.subjectProcessHazards) body.subjectProcessHazards = form.subjectProcessHazards;
  const eventSourceObligationKey = form.eventSourceObligationKey === '' ? null : form.eventSourceObligationKey;
  if (eventSourceObligationKey !== row.eventSourceObligationKey) body.eventSourceObligationKey = eventSourceObligationKey;
  if (!sameList(form.eventSourceVariantIds, row.eventSourceVariantIds)) body.eventSourceVariantIds = [...form.eventSourceVariantIds];
  const anchorSourceObligationKey = form.anchorSourceObligationKey === '' ? null : form.anchorSourceObligationKey;
  if (anchorSourceObligationKey !== row.anchorSourceObligationKey) body.anchorSourceObligationKey = anchorSourceObligationKey;

  if (Object.keys(body).length === 0) return null;

  const changeReason = nullableText(form.changeReason);
  if (changeReason !== null) body.changeReason = changeReason;
  return body;
}
